/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType } from 'react';

import { jsx } from '@compiled/react';

import { type ProductType } from '@atlassian/loom-utils';
import type { UniversalCreateSourceProduct } from '@atlassian/universal-create-common';

import type { CreationContext } from '../../../../types/crossAppCreation';
import { CrossAppItemBase, type CrossAppItemBaseProps } from '../CrossAppItemBase';

import { useLoomVideoConfig } from './hooks/useLoomVideoConfig';

export interface LoomVideoConfigProps extends CrossAppItemBaseProps {
	context: CreationContext;
	product: UniversalCreateSourceProduct;
}

export function convertSourceProductToProductType(
	product: UniversalCreateSourceProduct,
	subProduct?: string,
): ProductType {
	switch (product) {
		case 'townsquare': {
			if (subProduct === 'goal') {
				return 'GOAL';
			}
			if (subProduct === 'teams') {
				return 'TEAMS';
			}
			if (subProduct === 'home') {
				return 'HOME';
			}
			return 'TOWNSQUARE';
		}
		case 'confluence':
			return 'CONFLUENCE';
		case 'rovoExtension':
			return 'ROVO_EXTENSION';
		default:
			return 'UNKNOWN';
	}
}

/**
 * Higher-order component that wraps a component with Loom video configuration
 * @param WrappedComponent - The component to wrap with Loom video configuration
 * @returns A new component with Loom video configuration applied
 */
function withLoomVideoConfig<P extends LoomVideoConfigProps>(
	WrappedComponent: ComponentType<P>,
): ComponentType<LoomVideoConfigProps> {
	return function WithLoomVideoConfigComponent(props: LoomVideoConfigProps) {
		const { context, product } = props;

		const customisedItemConfig = useLoomVideoConfig({
			cloudId: context.cloudId,
			productName: convertSourceProductToProductType(product),
			environment: context.environment || 'prod',
			onItemClick: context.onItemClick,
		});

		const finalItemConfig = {
			...customisedItemConfig,
		};

		return <WrappedComponent {...(props as P)} customisedItemConfig={finalItemConfig} />;
	};
}

/**
 * LoomVideoMenuItem component that renders a Loom video cross-app item
 * Uses the withLoomVideoConfig HOC to apply Loom-specific configuration
 */
export const LoomVideoItem = withLoomVideoConfig(CrossAppItemBase);
